import * as THREE from 'three';
import { useMemo, useLayoutEffect, useRef } from 'react';
import { getHeight, isRoad, isRiver } from './Terrain';

const ROCK_COUNT = 80;
const MAP_SIZE = 400; // Match new world size

export const Rocks = () => {
    const meshRef = useRef<THREE.InstancedMesh>(null);
    const dummy = useMemo(() => new THREE.Object3D(), []);

    // Geometry: Low Poly Rock (Dodecahedron)
    const geometry = useMemo(() => {
        const geo = new THREE.DodecahedronGeometry(1, 0); // detail=0 for low poly
        geo.computeVertexNormals();
        return geo;
    }, []);

    // Material: Grey Rock
    const material = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#555555',
        roughness: 0.8,
        metalness: 0.1,
        flatShading: true
    }), []);

    useLayoutEffect(() => {
        if (!meshRef.current) return;

        let idx = 0;
        const maxAttempts = ROCK_COUNT * 5;

        for (let i = 0; i < maxAttempts; i++) {
            if (idx >= ROCK_COUNT) break;

            const x = (Math.random() - 0.5) * MAP_SIZE;
            const z = (Math.random() - 0.5) * MAP_SIZE;

            // Avoid spawning in center village or far edges
            const dist = Math.sqrt(x * x + z * z);
            if (dist < 30 || dist > 190) continue;

            if (isRoad(x, z) || isRiver(x, z)) continue;

            const y = getHeight(x, z);

            // Randomize transform
            const scale = 0.5 + Math.random() * 1.5;
            // Embed slightly to avoid floating on slopes due to mesh interpolation
            dummy.position.set(x, y - scale * 0.2, z); 
            dummy.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
            dummy.scale.setScalar(scale);
            dummy.updateMatrix();

            meshRef.current.setMatrixAt(idx++, dummy.matrix);
        }

        meshRef.current.count = idx;
        meshRef.current.instanceMatrix.needsUpdate = true;
    }, []);

    return (
        <instancedMesh
            ref={meshRef}
            args={[geometry, material, ROCK_COUNT]}
            castShadow
            receiveShadow
        />
    );
};
